import {
    App, 
} from 'antd';
import {useCallback, useContext, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import { getContracts } from "../../api/contracts";
import {getMessages} from "../../api/message";
import MainLayout from "../../components/layout/MainLayout";
import MyVehiclePanel from "../../components/panels/MyVehiclePanel";
import { showApiError } from "../../utility/error/error-handler";
import {Outlet} from "react-router-dom";
import './Home.css';
import { storeSessionData, getSessionData, getMyVehicleTitleKey } from '../../utility/global-util';
import { subscribeDevice } from '../../utility/webpush-util';
import HeaderBanner from "../../components/layout/HeaderBanner";
import GlobalContext from "../../GlobalContext";
import {
    getHistory,
    getJobsAwaitingDriverReview,
    getMaintenanceRecalls,
    getMaintenanceServices
} from "../../api/maintenance";
import {getLastOdo, getOpenTravelRequests, getVehicleAncillaries, getVehicleDetails} from "../../api/vehicle";
import {getTranslationLocales} from "../../api/config";
import {getLicenceCategories} from "../../api/driver";
import {getIncidentSummaries} from "../../api/im";
import useOnlineStatus from "../../utility/hooks/useOnlineStatus";
import { getQuotes } from '../../api/quotes';

// Load key extra data for the current vehicle and other vehicles so it is cached for offline use
//-----------------------------------------------------------------------------------------------
const loadExtraData = async (loadCurrentVehicle, loadAllVehicles) => {

    // NON -VEHICLE data (make sure works with vehicles are not allocated)
    console.log('..loading non vehicle data');

    // Add to retrieval list as needed
    //--------------------------------
    getTranslationLocales().then(ignore => {}).catch(ignore => {});
    getLicenceCategories().then(ignore => {}).catch(ignore => {});

    if (loadCurrentVehicle && loadAllVehicles) {

        // CURRENT vehicle key data loaded by the opening of the home page, extra data loaded here
        // - some view buttons and other key pages - my details and vehicle details
        console.log('..loading extra data for current vehicle: ' + loadCurrentVehicle.regNo + ' (' + loadCurrentVehicle.vehicleId + ')');

        // Add to retrieval list as needed
        //--------------------------------
        getMaintenanceServices(loadCurrentVehicle.vehicleId, true).then(ignore => {}).catch(ignore => {});
        getHistory(loadCurrentVehicle.vehicleId).then(ignore => {}).catch(ignore => {});
        getVehicleDetails(loadCurrentVehicle.vehicleId).then(ignore => {}).catch(ignore => {});

        // OTHER vehicles key and extra data loaded here
        // - all loaded data on home (useEffects)
        // - some view buttons and other key pages - my details and vehicle details
        loadAllVehicles.forEach((vehicle) => {
            if (vehicle.vehicleId !== loadCurrentVehicle.vehicleId) {
                console.log('..loading key and extra data for other vehicle: ' + vehicle.regNo + ' (' + vehicle.vehicleId + ')');

                // Add to retrieval list as needed
                //--------------------------------
                //current event not available offline
                getMaintenanceRecalls(vehicle.vehicleId).then(ignore => {}).catch(ignore => {});
                getMaintenanceServices(vehicle.vehicleId, true).then(ignore => {}).catch(ignore => {});
                getMaintenanceServices(vehicle.vehicleId, false).then(ignore => {}).catch(ignore => {});
                getJobsAwaitingDriverReview(vehicle.vehicleId).then(ignore => {}).catch(ignore => {});
                getVehicleAncillaries(vehicle.vehicleId).then(ignore => {}).catch(ignore => {});
                getHistory(vehicle.vehicleId).then(ignore => {}).catch(ignore => {});
                getIncidentSummaries(vehicle.vehicleId).then(ignore => {}).catch(ignore => {});
                getLastOdo(vehicle.vehicleId).then(ignore => {}).catch(ignore => {});
                getOpenTravelRequests(vehicle.vehicleId).then(ignore => {}).catch(ignore => {});
                getVehicleDetails(vehicle.vehicleId).then(ignore => {}).catch(ignore => {});
            }
        });
    }
}

const Home = () => {
    const { t } = useTranslation();

    const { notification } = App.useApp();

    const globalData = useContext(GlobalContext);

    const { isOnline } = useOnlineStatus();

    // Available in child pages through context
    const [permissions, setPermissions] = useState([]);
    const [vehicleId, setVehicleId] = useState(null);
    const [vehicle, setVehicle] = useState(null);  //accessed on child pages using context
    const [refreshPage, setRefreshPage] = useState(false); //accessed on child pages using context

    // My Vehicle header control (comes from route settings in driverRoutes.js)
    const [headerCustomTitle, setHeaderCustomTitle] = useState(null);    //default null (use standard heading)
    const [headerShow, setHeaderShow] = useState(true);          //default to show
    const [headerReadOnly, setHeaderReadOnly] = useState(false); //default not read only

    useEffect(() => {
        setHeaderCustomTitle(globalData.vehicleHeaderTrans ? t(globalData.vehicleHeaderTrans) : null);
        if (globalData.vehicleHeader) {
            if (globalData.vehicleHeader === 'off') {
                setHeaderShow(false);
                setHeaderReadOnly(false);
            } else if (globalData.vehicleHeader === 'readOnly') {
                setHeaderShow(true);
                setHeaderReadOnly(true);
            } else if (globalData.vehicleHeader === 'on') {
                setHeaderShow(true);
                setHeaderReadOnly(false);
            }
        } else {  //default
            setHeaderShow(true);
            setHeaderReadOnly(false);
        }
    }, [globalData.vehicleHeader, globalData.vehicleHeaderTrans, t]);

    // Update contract (vehicle)
    const updateContract = (contract) => {
        setVehicle(contract.contractVehicle);
        storeSessionData('currentContract', contract);
    }

    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageLoading, setPageLoading] = useState(isOnline); //load page with extra loading phase if online
    const [notifyCount, setNotifyCount] = useState(0);
    const [notifications, setNotifications] = useState([]);

    // Call function (above) to load data for the current and other vehicles
    const loadCachedData = useCallback((lContracts) => {
        let lVehicles = null;
        let lCurrentVehicle = null;
        if (lContracts && Array.isArray(lContracts) && lContracts.length > 0) {
            lVehicles = lContracts.map(contract => contract.contractVehicle);
            var index = 0;
            if(globalData && globalData.contractNo)
            {
                index = lContracts.map((contract) => contract.contractNo).indexOf(globalData.contractNo);
                globalData.setContractNo(null);
            }
            lCurrentVehicle = lVehicles[index]; //current one first
        }
        loadExtraData(lCurrentVehicle, lVehicles)
            .then(() => { console.log('..cached data loaded') })
            .catch(err => { console.error('ERROR loading cached data', err) });
    }, [contracts, globalData]);  //(keep dependency contracts) dependent on global contracts changing

    const refreshNotifications = useCallback(() => {
        console.log("..refreshing notifications");
        const messages = [];
        let unreadCount = 0;
        function getContract(vehicleId) {
            let result = null;
            for(let i = 0; i < contracts.length; i++)
            {
                if(contracts[i].contractVehicle.vehicleId === vehicleId)
                {
                    result = contracts[i];
                    break;
                }
            }
            return result;
        }
        getMessages().then(res => {
            res.map(item => {

                // Update unread count
                if(item.status === 'UNREAD')
                {
                    unreadCount = unreadCount + 1;
                }

                // Get vehicle contract
                const vehicleContract = getContract(item.data.VehicleId);

                // Set read only status for some messages based on online status and other factors
                let readOnlyFlag = false;
                if (item.type === 'MOT_EXPIRY' && !isOnline) {
                    readOnlyFlag = true;
                }

                // Add to messages array
                messages.push({
                    key: item.id,
                    header: item.header,
                    message: item.description,
                    status: item.status,
                    type: item.type,
                    data: item.data,
                    contract : vehicleContract,
                    readOnly: readOnlyFlag,
                });
                return "";
            })
            setNotifications(messages);
            setNotifyCount(unreadCount);
          });
    }, [contracts, isOnline]);

    // Page loading whirlygig - for home this is set at the end of the current event load
    useEffect(() => {
        if (globalData && globalData.currentRouteId && globalData.currentRouteId !== 'start' && globalData.currentRouteId !== 'home') {
            setPageLoading(false);
        }
    }, [globalData, globalData?.currentRouteId]);

    // Load contracts (vehicles)
    useEffect(() => {
        getContracts()
            .then(res => {
                setLoading(false);
                if (res && res.length > 0) {
                    // Allocated contracts
                    let loadedContracts = [];
                    let currentContract = getSessionData('currentContract');
                    if (currentContract) {
                        // Unset current contract if no longer valid
                        const findContract = res.find(contr => contr.contractNo === currentContract.contractNo);
                        if (findContract == null) {
                            currentContract = null;
                            storeSessionData('currentContract', null);
                        }
                    }
                    if (currentContract == null) {
                        // No current contract - update to the first one and add all contracts
                        var index = 0;
                        if(globalData && globalData.contractNo)
                        {
                            index = res.map((contract) => contract.contractNo).indexOf(globalData.contractNo);
                            globalData.setContractNo(null);
                        }
                        updateContract(res[index]);
                        loadedContracts.push(...res);
                    } else {
                        // Current contract - set it first, then the rest, for better loading of the vehicle banner
                        setVehicle(currentContract.contractVehicle);
                        const firstContract = res.find(contr => contr.contractNo === currentContract.contractNo);
                        if (firstContract) {
                            loadedContracts.push(firstContract); //add current contract first
                            const firstIndex = res.indexOf(firstContract);
                            res.splice(firstIndex, 1); //remove from retrieved contracts
                        }
                        loadedContracts.push(...res); //add rest in order retrieved
                    }
                    storeSessionData('driverHasAllocContracts', true);
                    setContracts(loadedContracts);
                    loadCachedData(loadedContracts);  //load data to be cached for offline use for current vehicle and other vehicles
                } else {
                    // No contracts allocated to driver
                    setContracts([]);
                    loadCachedData(null);  //load non vehicle data only
                    storeSessionData('currentContract', null);
                    storeSessionData('driverHasAllocContracts', false);
                }
            }, )
            .catch(err => showApiError(t(getMyVehicleTitleKey()), err, notification));
      }, [t, notification]); //(missing dependency) do not include globalData or loadCachedData as keeps happening

    useEffect(() => setPermissions(getSessionData('driverPermissions')), [setPermissions]); //accessed on child pages using context
    
    useEffect(() => refreshNotifications(), [contracts, refreshPage, refreshNotifications]);

    const changeContract = (contract) => {
        // Change contract if changed by user (stops when navigating and has not been a contract change)
        const currContract = getSessionData('currentContract');
        if (contract?.contractNo !== currContract?.contractNo) {
            updateContract(contract);

            // Special page loading on home page (changing contract or going home - and only if online)
            if (isOnline && !pageLoading && globalData && globalData.currentRouteId && globalData.currentRouteId === 'home') {
                setPageLoading(true);
            }
        }
    };

    const userCustomer = getSessionData('selectedCustomer');
    let userCustomerAccount = '';
    let userCustomerEntity = '';
    if (userCustomer) {
        userCustomerAccount = userCustomer.code ? userCustomer.code : '';
        userCustomerEntity = userCustomer.entityId ? userCustomer.entityId : '';
    }

    // This getQuotes facilitates displaying the view quotes panel(when quotes exist) or create new quote panel (no quotes available)
    useEffect(() => {
      if (isOnline) {
        if (userCustomerEntity && userCustomerAccount) {
          getQuotes(userCustomerEntity, 'C', userCustomerAccount)
              .then(res => {
                setLoading(false);
                storeSessionData('driverQuotes', res);
              },)
              .catch(err => {
                  // Show error if online otherwise show dash if offline (not been on this vehicle or cache not working)
                  showApiError('', err, notification);
              });
        }
      } 
    }, [t, notification, userCustomerEntity, userCustomerAccount, isOnline]);    

    useEffect(() => {
        if(isOnline && "Notification" in window)
        {
            if(Notification.permission === 'default')
            {
                subscribeDevice((result) => {console.log(result)});
            }
        }
    }, [isOnline]); 

    return (
        <MainLayout
            showSubHeader={true}
            startHigher={window.innerWidth < 769 ? "100px" : "210px"}
            subHeaderContent=
            {
                headerShow
                ? 
                <MyVehiclePanel contracts={contracts}
                                  currentVehicleId={vehicleId}
                                  loading={loading}
                                  pageLoading={pageLoading}
                                  onChange={changeContract}
                                  readOnly={headerReadOnly}
                                  customTitle={headerCustomTitle}
                                  setRefreshPage={setRefreshPage}
                                  />
                
                : 
                <HeaderBanner />
            }
            subHeaderClass={headerShow ? undefined : 'dp-full-no-vehicle' }
            footerShowReturnToTop={true}
            permissions={permissions}
            vehicleChange={(contract) => {updateContract(contract);}}
            notifyCount={notifyCount}
            notifications={notifications}
            refreshNotifications={refreshNotifications}
        >
            <Outlet context={{vehicle: vehicle, 
                              setVehicleId: setVehicleId, 
                              setPageLoading: setPageLoading, 
                              permissions: permissions, 
                              refreshPage: refreshPage, 
                              setRefreshPage: setRefreshPage,
                              refreshNotifications: refreshNotifications}}/>
        </MainLayout>
    );
}

export default Home;